import { observer } from "mobx-react-lite";
import React, { useContext, useEffect } from "react";
import { Context } from "../../index";
import { Helmet } from "react-helmet";

import styles from "./bonus-program.module.scss";
import BlockQuestion from "../../components/faq/BlockQuestion";
import classNames from "classnames";
import { PurchaseIncentives } from "./PurchaseIncentives";
import { LoyaltyTier } from "./LoyaltyTier";
import { ReceivingContent } from "./ReceivingContent";

const loyaltyTiers = [
  {
    level: "CLASSIC",
    cashbackPercent: "3%",
    purchaseAmount: "до 50 000 ₽",
    bonusPaymentPercent: "до 30%",
    imageDesktop: "/files/images/bonus-program/tier-1.png",
    imageMobile: "/files/images/bonus-program/tier-1.png",
    benefits: [
      "Приоритетная информация о распродажах.",
      "Возможность получать (стилистические гайды, lookbook).",
      "Персонализированные рекомендации товаров.",
    ],
    activePointIndex: 1,
  },
  {
    level: "PRO",
    cashbackPercent: "5%",
    purchaseAmount: "до 150 000 ₽",
    bonusPaymentPercent: "до 30%",
    imageDesktop: "/files/images/bonus-program/tier-2.png",
    imageMobile: "/files/images/bonus-program/tier-2.png",
    benefits: [
      "Ранний доступ к новым коллекциям (pre-order).",
      "Эксклюзивные мини-капсулы для участников уровня.",
      "Приглашение на офлайн-ивенты.",
    ],
    activePointIndex: 2,
  },
  {
    level: "FASHION EXPERT",
    cashbackPercent: "7%",
    purchaseAmount: "до 300 000 ₽",
    bonusPaymentPercent: "до 30%",
    imageDesktop: "/files/images/bonus-program/tier-3.png",
    imageMobile: "/files/images/bonus-program/tier-3.png",
    benefits: [
      "Персональный стилист (онлайн-консультации).",
      "Эксклюзивные лимитированные коллекции.",
      "Доступ к VIP-ивентам бренда.",
    ],
    activePointIndex: 3,
  },
  {
    level: "FASHION DIAMOND",
    cashbackPercent: "10%",
    purchaseAmount: "от 300 000 ₽",
    bonusPaymentPercent: "до 30%",
    imageDesktop: "/files/images/bonus-program/tier-4.png",
    imageMobile: "/files/images/bonus-program/tier-4.png",
    benefits: [
      "Индивидуальный менеджер для поддержки и консультаций.",
      "Эксклюзивный доступ к новым коллекциям и коллаборациям.",
      "Приглашения на закрытые показы и fashion-вечера.",
      "Персональные подарки от бренда на праздники.",
    ],
    activePointIndex: 4,
  },
];

export const BonusProgram = observer(() => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { settings, user } = useContext(Context);

  const openAuthModalFunction = () => {
    user.setOpenAuthModal(1);
    user.setShadowOverlay(true);
    user.setStepModals("registration");
  };

  return (
    <div>
      <Helmet>
        <title>Программа бонусов</title>
        <meta
          name="description"
          content="Политика конфиденциальности интернет-магазина YOU WANNA. По имеющимся вопросам звоните: ☎ +7 (926) 887-70-07"
        />
      </Helmet>
      <section className={styles.mainScreen}>
        <div className={styles.container}>
          <div className={styles.mainBannerWrap}>
            <picture>
              <source
                media="(max-width: 640px)"
                srcSet="/files/images/bonus-program/bonus-program-logo-390-3.png"
              />

              <source
                media="(max-width: 850px)"
                srcSet="/files/images/bonus-program/bonus-program-logo-834-3.png"
              />

              <source
                media="(max-width: 1440px)"
                srcSet="/files/images/bonus-program/bonus-program-logo-desktop-3.png"
              />

              <img
                src="/files/images/bonus-program/bonus-program-logo-desktop-3.png"
                alt="YOU WANNA Club"
              />
            </picture>
          </div>
          <div className={styles.mainScreenText}>
            <p>
              <strong>Добро пожаловать <span>в бонусную программу YOU WANNA.</span></strong>
            </p>
            <p>
              Совершайте покупки, накапливайте бонусы и пользуйтесь
              многочисленными{' '}
            </p>
            <p>
              преимуществами клуба на сайте, в мобильном приложении и в
              розничных магазинах.
            </p>
          </div>
          {user._isAuth === false && (
            <div className={styles.mainScreenButtonWrap}>
              <button
                onClick={openAuthModalFunction}
                // className={styles.becomeMember}
                className={classNames(
                  "button",
                  "button--primary",
                  "button--inline",
                  "button--default",
                  "button--noradius",
                  styles.mainScreenButton
                )}
              >
                Стать участником / Войти
              </button>
            </div>
          )}
        </div>
      </section>

      <section className={styles.purchaseIncentives}>
        <div className={styles.container}>
          <PurchaseIncentives
            list={[
              {
                title: "до 10%",
                description: "Кешбэк бонусами",
              },
              {
                title: "до 30%",
                description: "Оплаты бонусами",
              },
              {
                title: "100%",
                description: "1 бонус = 1 рубль",
              },
            ]}
          />
        </div>
      </section>

      <section className={styles.typeSection}>
        <div className={styles.container}>
          <div className={styles.typeWrapper}>
            {loyaltyTiers.map((tier) => (
              <LoyaltyTier
                key={tier.activePointIndex}
                activePointIndex={tier.activePointIndex}
                {...tier}
              />
            ))}
          </div>
          <div className={styles.typeSectionButtonWrapper}>
            <a
              href="/files/pdf/Описание программы лояльности.pdf"
              className={classNames(
                "button",
                "button--primary",
                "button--inline",
                "button--default",
                "button--noradius",
                styles.typeSectionButton
              )}
              rel="noreferrer"
              target="_blank"
            >
              Подробные условия бонусной программы
            </a>
          </div>
        </div>
      </section>

      <ReceivingContent
        isAuth={user._isAuth}
        openAuthModalFunction={openAuthModalFunction}
      />

      <section className={styles.questionsSectionNew}>
        <div className={styles.questionsSection}>
          <div className={styles.container}>
            <div className={styles.questionsWrapper}>
              <div className={styles.questionsList}>
                <h2 className={styles.subtitle}>Частые вопросы:</h2>

                <div>
                  <BlockQuestion
                    item={{
                      titleFqa: "Как я могу получить бонусы?",
                      textFqa: (
                        <>
                          Вы&nbsp;получаете бонусы за&nbsp;покупки
                          в&nbsp;розничных магазинах, на&nbsp;сайте
                          и&nbsp;в&nbsp;мобильном приложении. Также
                          вы&nbsp;можете получить бонусы за&nbsp;заполнение
                          анкетных данных в&nbsp;профиле, участие
                          в&nbsp;специальных акциях и&nbsp;многое другое. Бонусы
                          будут доступны к&nbsp;списанию через 14&nbsp;дней
                          после покупки.
                        </>
                      ),
                    }}
                  />
                  <BlockQuestion
                    item={{
                      titleFqa: "Как мне потратить бонусы?",
                      textFqa: (
                        <>
                          Бонусами можно оплатить до&nbsp;30% от&nbsp;стоимости
                          товаров представленных по&nbsp;полной цене
                          в&nbsp;нашем каталоге. Скидка бонусами
                          не&nbsp;распространяется на&nbsp;покупки подарочных
                          сертификатов.
                        </>
                      ),
                    }}
                  />
                  <BlockQuestion
                    item={{
                      titleFqa: "Как узнать свой бонусный баланс?",
                      textFqa: (
                        <>
                          Актуальный бонусный счет находится в&nbsp;профиле
                          на&nbsp;<a href="https://you-wanna.ru/">сайте</a> или
                          в&nbsp;мобильном приложении в&nbsp;разделе &laquo;Мои
                          бонусы&raquo;. Авторизуйтесь, чтобы узнать, сколько
                          бонусов доступно для списания.
                        </>
                      ),
                    }}
                  />
                  <BlockQuestion
                    item={{
                      titleFqa: <>Как я&nbsp;буду узнавать об&nbsp;акциях?</>,
                      textFqa: (
                        <>
                          Мы&nbsp;используем разные каналы коммуникации для
                          оповещения о&nbsp;старте новых акций: email, смс,
                          push-уведомления в&nbsp;мобильном приложении,
                          всплывающие окна на&nbsp;сайте.
                        </>
                      ),
                    }}
                  />
                  <BlockQuestion
                    item={{
                      titleFqa: (
                        <>Еще остались вопросы, куда я&nbsp;могу обратиться?</>
                      ),
                      textFqa: (
                        <>
                          По&nbsp;всем вопросам бонусной программы
                          вы&nbsp;можете обратиться по&nbsp;
                          <a href="tel:+79268877007">телефону</a>&nbsp;, в&nbsp;
                          <a href="https://api.whatsapp.com/send/?phone=79268877007">
                            группу WhatsApp
                          </a>{" "}
                          или написать на&nbsp;
                          <a href="mailto:info@you-wanna.ru">почту</a>.
                        </>
                      ),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
});
